import React, { useState, useEffect } from 'react';
import { fetchGalleryBySlug, getOptimizedImageUrl } from '../lib/cms';
import { getImageAlt } from '../lib/images';
import Lightbox from './Lightbox';

const DEFAULT_GALLERIES = {
  'klinikken': {
    title: 'Klinikken & Omgivelser',
    slug_id: 'klinikken',
    description: 'Stemningsbilleder fra Cryo by Breums lokaler på Dalumvej i Odense SV.',
    images: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-ems-studio-behandlingsrum.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-klinik-behandlingsrum-briks.jpg'
    ]
  },
  'fedtfrysning': {
    title: 'Fedtfrysning Galleri',
    slug_id: 'fedtfrysning',
    description: 'Billeder fra behandlingsrum og udstyr til fedtfrysning hos Cryo by Breum i Odense.',
    images: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-fedtfrysning-behandling-applikatorer.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-klinik-behandlingsrum-briks.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg'
    ]
  },
  'ems-traening': {
    title: 'EMS Træning Galleri',
    slug_id: 'ems-traening',
    description: 'Billeder fra EMS studiet og guidede træningssessioner.',
    images: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-ems-studio-behandlingsrum.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-kryosauna-behandling-mand.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-kryosauna-behandling-kvinde.jpg'
    ]
  },
  'body-roll': {
    title: 'Body Roll Galleri',
    slug_id: 'body-roll',
    description: 'Billeder af Body Roll rullestudio og infrarødt velvære.',
    images: [
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-body-roll-massage-maskine.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-ems-studio-behandlingsrum.jpg',
      'https://assets.auxodev.dk/assets/bbc8c269-a12e-46e2-947b-0cefa0025eea/cryo-by-breum-reception-og-ventevaerelse.jpg'
    ]
  }
};

export default function GallerySection({ gallerySlug, title = 'Galleri & Stemning', subtitle = 'Et kig ind i behandlingsuniverset hos Cryo by Breum' }) {
  const [gallery, setGallery] = useState(() => (gallerySlug && DEFAULT_GALLERIES[gallerySlug]) || null);
  const [loading, setLoading] = useState(() => !gallerySlug || !DEFAULT_GALLERIES[gallerySlug]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let mounted = true;
    async function loadGallery() {
      if (!gallerySlug) return;
      if (!DEFAULT_GALLERIES[gallerySlug]) {
        setLoading(true);
      }
      const data = await fetchGalleryBySlug(gallerySlug);
      if (mounted) {
        if (data) {
          setGallery(data);
        }
        setLoading(false);
      }
    }
    loadGallery();
    return () => {
      mounted = false;
    };
  }, [gallerySlug]);

  const images = gallery?.images || [];

  if (loading && !gallery) {
    return (
      <section className="py-16 bg-sand-light/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-sand-dark rounded w-48 mx-auto"></div>
            <div className="h-8 bg-sand-dark rounded w-80 mx-auto"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
              <div className="h-64 bg-sand rounded-xl"></div>
              <div className="h-64 bg-sand rounded-xl"></div>
              <div className="h-64 bg-sand rounded-xl"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (images.length === 0) {
    return null;
  }

  const handleOpenLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const displayTitle = gallery?.title || title;
  const displaySubtitle = gallery?.description || subtitle;

  return (
    <section className="py-20 bg-white border-y border-taupe-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 reveal-on-scroll">
          <h2 className="font-sans text-2xl sm:text-3xl lg:text-4xl font-semibold text-espresso tracking-wider uppercase">
            {displayTitle}
          </h2>
          <p className="text-taupe text-sm sm:text-base mt-3 leading-relaxed">
            {displaySubtitle}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((imgUrl, idx) => (
            <div 
              key={idx} 
              className={`reveal-on-scroll ${idx === 1 ? 'delay-150' : idx === 2 ? 'delay-300' : ''}`}
            >
              <button
                type="button"
                onClick={() => handleOpenLightbox(idx)}
                className="group relative h-72 sm:h-80 w-full overflow-hidden bg-sand cursor-pointer focus:outline-hidden transition-transform duration-300 hover:scale-[1.01]"
                aria-label={`Åbn billede ${idx + 1} i fuld skærm`}
              >
                <img
                  src={getOptimizedImageUrl(imgUrl, { width: 800, quality: 85 })}
                  alt={getImageAlt(imgUrl, `${displayTitle} - billede ${idx + 1}`)}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/35 transition-colors duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-2xl drop-shadow-md">
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                  </span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        images={images}
        currentIndex={currentIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </section>
  );
}
